import { useEffect, useState } from 'react';
import { useAudioStore, useHandStore, useUiStore } from '@store/index';
import './StartOverlay.css';

interface StartOverlayProps {
  onStart: () => void;
}

type Phase = 'idle' | 'starting' | 'ready' | 'dismissed';

/**
 * Blocks the stage until the user explicitly taps. This single tap:
 *   1) unlocks the AudioContext in the same synchronous gesture stack
 *   2) requests camera permission
 *   3) boots MediaPipe
 *
 * When audio is initialized AND camera is streaming AND a hand is
 * detected, we briefly show "Ready to play" then dismiss.
 */
export function StartOverlay({ onStart }: StartOverlayProps) {
  const started = useUiStore((s) => s.started);
  const tunerOpen = useUiStore((s) => s.tunerOpen);
  const audioReady = useAudioStore((s) => s.initialized);
  const cameraStatus = useHandStore((s) => s.cameraStatus);
  const [phase, setPhase] = useState<Phase>('idle');

  useEffect(() => {
    if (!started) {
      setPhase('idle');
      return;
    }
    // Tuner-mode boot path: camera stays off (tuner owns the mic),
    // so the "ready" gate drops the camera-streaming requirement.
    // Audio-ready is still required because the AudioContext must
    // be unlocked inside the user-gesture stack to play anything
    // after the tuner is closed.
    const micModeReady = tunerOpen && audioReady;
    const playModeReady = !tunerOpen && audioReady && cameraStatus === 'streaming';
    if ((micModeReady || playModeReady) && phase !== 'dismissed') {
      setPhase('ready');
      const t = setTimeout(() => setPhase('dismissed'), 900);
      return () => clearTimeout(t);
    }
    if (phase === 'idle') setPhase('starting');
  }, [started, audioReady, cameraStatus, tunerOpen, phase]);

  if (phase === 'dismissed') return null;

  const handleStart = () => {
    onStart();
    setPhase('starting');
  };

  return (
    <div className={`start-overlay start-overlay--${phase}`}>
      {phase === 'idle' && (
        <button
          type="button"
          className="start-overlay__cta"
          onClick={handleStart}
          onPointerDown={handleStart}
        >
          <span className="start-overlay__play">▶</span>
          <span className="start-overlay__label">
            {tunerOpen ? 'Tap to start tuner' : 'Tap to start'}
          </span>
          <span className="start-overlay__hint">
            {tunerOpen
              ? 'We’ll ask for microphone permission next'
              : 'We’ll ask for camera permission next'}
          </span>
        </button>
      )}

      {phase === 'starting' && (
        <div className="start-overlay__status">
          <div className="start-overlay__spinner" />
          <p>
            {tunerOpen && !audioReady && 'Starting audio…'}
            {tunerOpen && audioReady && 'Opening tuner…'}
            {!tunerOpen && cameraStatus === 'requesting' && 'Requesting camera…'}
            {!tunerOpen && cameraStatus === 'streaming' && !audioReady && 'Starting audio…'}
            {!tunerOpen && cameraStatus === 'idle' && 'Loading…'}
            {!tunerOpen && cameraStatus === 'denied' && 'Camera permission denied.'}
            {!tunerOpen && cameraStatus === 'error' && 'Camera error.'}
          </p>
        </div>
      )}

      {phase === 'ready' && (
        <div className="start-overlay__ready">
          <span className="start-overlay__check">✓</span>
          <p>Ready to play</p>
        </div>
      )}
    </div>
  );
}
